import { type KeyboardEvent, useCallback, useMemo } from "react";

export type UseListboxKeyboardParams = {
  open: boolean;
  itemCount: number;
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onSelect: (index: number) => void;
  onOpen: () => void;
  onClose: () => void;
  /** Stable per-option id factory so `aria-activedescendant` points at a real node. */
  getOptionId: (index: number) => string;
  /** When provided, Arrow/Home/End resolve to the nearest enabled index in the
   *  direction of travel instead of landing on a disabled option. */
  isItemDisabled?: (index: number) => boolean;
};

export type UseListboxKeyboardResult = {
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  /** The active option's id when the overlay is open with a valid selection. */
  activeDescendantId: string | undefined;
};

const wrap = (index: number, count: number): number =>
  count === 0 ? 0 : ((index % count) + count) % count;

// The `target !== currentTarget` guard ignores events bubbling from option
// children (mirrors `useInteractiveElement`).
export const useListboxKeyboard = ({
  open,
  itemCount,
  activeIndex,
  onActiveIndexChange,
  onSelect,
  onOpen,
  onClose,
  getOptionId,
  isItemDisabled,
}: UseListboxKeyboardParams): UseListboxKeyboardResult => {
  const moveActiveIndex = useCallback(
    (start: number, step: number) => {
      if (isItemDisabled === undefined) {
        onActiveIndexChange(wrap(start, itemCount));
        return;
      }
      for (let offset = 0; offset < itemCount; offset += 1) {
        const candidate = wrap(start + step * offset, itemCount);
        if (!isItemDisabled(candidate)) {
          onActiveIndexChange(candidate);
          return;
        }
      }
    },
    [isItemDisabled, itemCount, onActiveIndexChange],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.target !== event.currentTarget) {
        return;
      }

      switch (event.key) {
        case "ArrowDown": {
          if (!open) {
            event.preventDefault();
            onOpen();
            return;
          }
          if (itemCount === 0) {
            return;
          }
          event.preventDefault();
          moveActiveIndex(activeIndex + 1, 1);
          return;
        }
        case "ArrowUp": {
          if (!open) {
            event.preventDefault();
            onOpen();
            return;
          }
          if (itemCount === 0) {
            return;
          }
          event.preventDefault();
          moveActiveIndex(activeIndex - 1, -1);
          return;
        }
        case "Home": {
          if (!open || itemCount === 0) {
            return;
          }
          event.preventDefault();
          moveActiveIndex(0, 1);
          return;
        }
        case "End": {
          if (!open || itemCount === 0) {
            return;
          }
          event.preventDefault();
          moveActiveIndex(itemCount - 1, -1);
          return;
        }
        // Space selects like Enter for a select-only combobox (WAI-ARIA).
        case "Enter":
        case " ": {
          if (
            !open ||
            activeIndex < 0 ||
            activeIndex >= itemCount ||
            isItemDisabled?.(activeIndex)
          ) {
            return;
          }
          event.preventDefault();
          onSelect(activeIndex);
          return;
        }
        case "Escape": {
          if (!open) {
            return;
          }
          event.preventDefault();
          onClose();
          return;
        }
        default:
          return;
      }
    },
    [
      open,
      itemCount,
      activeIndex,
      isItemDisabled,
      moveActiveIndex,
      onSelect,
      onOpen,
      onClose,
    ],
  );

  const activeDescendantId = useMemo(() => {
    if (!open || activeIndex < 0 || activeIndex >= itemCount) {
      return undefined;
    }
    return getOptionId(activeIndex);
  }, [open, activeIndex, itemCount, getOptionId]);

  return { onKeyDown, activeDescendantId };
};
